/** Small helpers shared by the discovery-core modules. Not exported from the package. */

/** `1 source`, `2 sources`. */
export function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

const CODE_PARTS = /^(.*?)(\d+)$/;

/**
 * Natural order for system codes, so `S2` sorts before `S10` and `E-009`
 * before `E-010`. Codes without a trailing number sort by text.
 */
export function compareCodes(a: string, b: string): number {
  const ma = CODE_PARTS.exec(a);
  const mb = CODE_PARTS.exec(b);
  if (ma && mb && ma[1] === mb[1]) return Number(ma[2]) - Number(mb[2]);
  return a < b ? -1 : a > b ? 1 : 0;
}

/** A nullable code compared naturally, with missing codes last. */
export function compareOptionalCodes(
  a: string | null | undefined,
  b: string | null | undefined,
): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return compareCodes(a, b);
}

export function compareStrings(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** Milliseconds since the epoch for a Date or an ISO string; null when absent or unreadable. */
export function toTime(value: Date | string | null | undefined): number | null {
  if (value == null) return null;
  const t = value instanceof Date ? value.getTime() : Date.parse(value);
  return Number.isFinite(t) ? t : null;
}

/** Earliest time first; missing times last. */
export function compareTimes(
  a: Date | string | null | undefined,
  b: Date | string | null | undefined,
): number {
  const ta = toTime(a);
  const tb = toTime(b);
  if (ta == null && tb == null) return 0;
  if (ta == null) return 1;
  if (tb == null) return -1;
  return ta - tb;
}

/** Remove accents and other combining marks: `José` → `Jose`. */
export function foldDiacritics(value: string): string {
  return value.normalize('NFKD').replace(/\p{M}+/gu, '');
}

/** Clamp to [min, max]; non-finite values become `min`. */
export function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
}

/** Two decimal places, as confidence is stored and shown. */
export function round2(value: number): number {
  return Math.round(value * 100) / 100;
}
